import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  DndContext,
  useDraggable,
  useDroppable,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { useAuth } from "@/hooks/use-auth";
import { getDataSource } from "@/lib/data-source";
import { JOB_STATUSES, type Job, type JobStatus } from "@/lib/types";
import { StatusChip } from "@/components/StatusChip";
import { format } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { StaffTodaySummary } from "@/components/staff/StaffTodaySummary";
import { listCuttingTiers } from "@/lib/products";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/app/staff/jobs-board")({ component: Board });

const COLUMNS: JobStatus[] = JOB_STATUSES.filter((s) => s !== "Completed");

const COLUMN_DESCRIPTIONS: Record<JobStatus, string> = {
  New: "New job submitted",
  "Artwork Check": "Artwork pending review",
  "Gang Sheet": "Ready for gang sheet",
  "Queued for Print": "Queued for print",
  Printing: "In production",
  Curing: "Curing in progress",
  "Quality Check": "Quality inspection",
  "Ready for Dispatch": "Awaiting dispatch",
  Completed: "Order complete",
  "On Hold": "On hold",
};

function Board() {
  const nav = useNavigate();
  const { demoMode } = useDemoMode();
  const { profile } = useAuth();
  const ds = useMemo(
    () => getDataSource(profile?.role === "staff" && demoMode),
    [demoMode, profile?.role],
  );
  const qc = useQueryClient();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const getPreference = (key: string, defaultValue: any) => {
    try {
      const saved = localStorage.getItem(`flightdeck_${key}`);
      return saved !== null ? JSON.parse(saved) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const setPreference = (key: string, value: any) => {
    try {
      localStorage.setItem(`flightdeck_${key}`, JSON.stringify(value));
    } catch (e) {
      console.warn("Failed to save preference:", e);
    }
  };

  const [myJobsOnly, setMyJobsOnly] = useState(() => getPreference("myJobsOnly", false));
  const [brandFilter, setBrandFilter] = useState<"all" | "resolute" | "craftyjet">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [optimistic, setOptimistic] = useState<Record<string, JobStatus>>({});
  const [showArchived, setShowArchived] = useState(false);

  const handleMyJobsOnlyChange = (checked: boolean) => {
    setMyJobsOnly(checked);
    setPreference("myJobsOnly", checked);
  };

  const { data: jobs = [] } = useQuery({
    queryKey: ["staff-jobs", demoMode, showArchived],
    queryFn: () => ds.listJobs({ includeArchived: showArchived }),
  });
  const { data: cuttingTiers = [] } = useQuery({
    queryKey: ["cutting-tiers"],
    queryFn: listCuttingTiers,
  });
  const { data: staffProfiles = [] } = useQuery({
    queryKey: ["staff-profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .eq("role", "staff");
      if (error) throw error;
      return data ?? [];
    },
  });

  const grouped = useMemo(() => {
    const map: Record<string, Job[]> = {};
    for (const c of COLUMNS) map[c] = [];
    const filtered = jobs.filter((j) => {
      const isArchived = !!(j as { archived_at?: string | null }).archived_at;
      if (showArchived ? !isArchived : isArchived) return false;
      const brandMatch = brandFilter === "all" || j.customer?.brand === brandFilter;
      const searchMatch =
        !searchQuery ||
        j.job_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        j.customer?.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
      const myJobsMatch = !myJobsOnly || j.assignee_profile_id === profile?.id;
      return brandMatch && searchMatch && myJobsMatch;
    });
    for (const j of filtered) {
      const status = (optimistic[j.id] ?? j.status) as JobStatus;
      if (map[status]) map[status].push(j);
    }
    return map;
  }, [jobs, optimistic, brandFilter, searchQuery, myJobsOnly, profile?.id, showArchived]);

  const onDragEnd = async (e: DragEndEvent) => {
    const id = e.active.id as string;
    const newStatus = e.over?.id as JobStatus | undefined;
    if (!newStatus) return;
    const job = jobs.find((j) => j.id === id);
    if (!job || job.status === newStatus) return;
    setOptimistic((p) => ({ ...p, [id]: newStatus }));
    try {
      await ds.updateJobStatus(id, newStatus);
      toast.success(`Moved to ${newStatus}`);
      qc.invalidateQueries({ queryKey: ["staff-jobs", demoMode] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to move");
      setOptimistic((p) => {
        const c = { ...p };
        delete c[id];
        return c;
      });
    }
  };

  const handleAdvance = async (jobId: string, currentStatus: JobStatus) => {
    const currentIndex = COLUMNS.indexOf(currentStatus);
    const nextStatus = currentIndex < COLUMNS.length - 1 ? COLUMNS[currentIndex + 1] : null;
    if (!nextStatus) return;
    const job = jobs.find((j) => j.id === jobId);
    if (!job || job.status === nextStatus) return;
    setOptimistic((p) => ({ ...p, [jobId]: nextStatus }));
    try {
      await ds.updateJobStatus(jobId, nextStatus);
      toast.success(`Moved to ${nextStatus}`);
      qc.invalidateQueries({ queryKey: ["staff-jobs", demoMode] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to move");
      setOptimistic((p) => {
        const c = { ...p };
        delete c[jobId];
        return c;
      });
    }
  };

  return (
    <div className="space-y-5">
      <StaffTodaySummary />
      <div>
        <h1 className="text-3xl font-bold">Jobs board</h1>
        <p className="text-muted-foreground">Drag jobs across the floor.</p>
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center rounded-full border border-border bg-background p-0.5 gap-0.5">
          <button
            className="rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground"
            aria-current="page"
          >
            Board
          </button>
          <button
            onClick={() => nav({ to: "/app/staff/jobs-table", search: { filter: undefined } })}
            className="rounded-full px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Table
          </button>
        </div>
        <select
          className="rounded-full border border-border bg-background px-3 py-1.5 text-sm"
          value={brandFilter}
          onChange={(e) => setBrandFilter(e.target.value as "all" | "resolute" | "craftyjet")}
        >
          <option value="all">All brands</option>
          <option value="resolute">Resolute</option>
          <option value="craftyjet">CraftyJet</option>
        </select>
        <input
          type="text"
          placeholder="Search jobs..."
          className="rounded-full border border-border bg-background px-3 py-1.5 text-sm w-full md:w-64"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <label className="flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={myJobsOnly}
            onChange={(e) => handleMyJobsOnlyChange(e.target.checked)}
            className="rounded"
          />
          My jobs only
        </label>
        <label
          className={
            "flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm cursor-pointer transition-colors " +
            (showArchived
              ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
              : "border-border bg-background")
          }
        >
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="rounded"
          />
          Show archived
        </label>
      </div>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="-mx-2 flex gap-3 overflow-x-auto pb-4">
          {COLUMNS.map((status) => (
            <Column
              key={status}
              status={status}
              jobs={grouped[status] ?? []}
              cuttingTiers={cuttingTiers}
              staffProfiles={staffProfiles}
              onAdvance={handleAdvance}
            />
          ))}
        </div>
      </DndContext>
    </div>
  );
}

function Column({
  status,
  jobs,
  cuttingTiers,
  staffProfiles,
  onAdvance,
}: {
  status: JobStatus;
  jobs: Job[];
  cuttingTiers: any[];
  staffProfiles: any[];
  onAdvance: (jobId: string, currentStatus: JobStatus) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex w-72 shrink-0 flex-col rounded-3xl border bg-card p-3 transition-colors",
        isOver ? "border-primary bg-primary/5" : "border-border",
      )}
    >
      <div className="mb-2 px-1">
        <div className="flex items-center justify-between">
          <StatusChip status={status} />
          <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-foreground">
            {jobs.length}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{COLUMN_DESCRIPTIONS[status] || ""}</p>
      </div>
      <div className="space-y-2">
        {jobs.map((j) => (
          <Card
            key={j.id}
            job={j}
            cuttingTiers={cuttingTiers}
            staffProfiles={staffProfiles}
            onAdvance={onAdvance}
          />
        ))}
        {jobs.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground">
            Drop here
          </div>
        )}
      </div>
    </div>
  );
}

function Card({
  job,
  cuttingTiers,
  staffProfiles,
  onAdvance,
}: {
  job: Job;
  cuttingTiers: any[];
  staffProfiles: any[];
  onAdvance: (jobId: string, currentStatus: JobStatus) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: job.id });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(1.02)` }
    : undefined;

  const cuttingTier = cuttingTiers.find((t) => t.id === job.cutting_tier_id);
  const isOverdue = job.due_date && new Date(job.due_date) < new Date();
  const currentIndex = COLUMNS.indexOf(job.status as JobStatus);
  const nextStatus = currentIndex < COLUMNS.length - 1 ? COLUMNS[currentIndex + 1] : null;
  const assignee = staffProfiles.find((p) => p.id === job.assignee_profile_id);

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={style}
      className={cn(
        "rounded-2xl border border-border bg-background p-3 shadow-soft transition-all duration-200",
        isDragging && "opacity-70 shadow-lg ring-2 ring-primary ring-offset-2 scale-[1.02]",
      )}
    >
      <Link
        to="/app/staff/jobs/$id"
        params={{ id: job.id }}
        onClick={(e) => isDragging && e.preventDefault()}
      >
        <div className="space-y-2">
          <div className="text-sm font-semibold">{job.job_title}</div>
          <div className="text-xs text-muted-foreground">{job.customer?.company_name ?? "—"}</div>
          <div className="flex items-center gap-2 text-xs">
            <span className="rounded-full bg-secondary px-2 py-0.5">{job.product_type ?? "—"}</span>
            {cuttingTier && (
              <span className="rounded-full bg-accent/10 px-2 py-0.5 text-accent">
                {cuttingTier.name}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="rounded-full bg-secondary px-2 py-0.5">Qty {job.quantity}</span>
            {job.due_date && (
              <span
                className={cn(isOverdue ? "text-red-600 font-medium" : "text-muted-foreground")}
              >
                {format(new Date(job.due_date), "d MMM")}
                {isOverdue && " (overdue)"}
              </span>
            )}
          </div>
          {assignee && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Assignee:</span> {assignee.full_name ?? assignee.email}
            </div>
          )}
        </div>
      </Link>
      {nextStatus && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAdvance(job.id, job.status as JobStatus);
          }}
          className="mt-2 w-full rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
        >
          Advance to {nextStatus}
        </button>
      )}
    </div>
  );
}
